import * as Yup from 'yup';

import { CustomerFormData, ContactInfoType } from '../application/types/CustomerTypes';
import { LocationFormValidationSchema } from '@/map/presentation/LocationForm/validationSchema';
import { EMAIL_REGEX, PHONE_REGEX } from '@/common/application/utils/stringUtils';

// Phone-related contact types for validation
const PHONE_CONTACT_TYPES = [ContactInfoType.PHONE, ContactInfoType.MOBILE, ContactInfoType.LANDLINE, ContactInfoType.FAX];

// Validation schema for the full customer form (CustomerDetails page)
export const CustomerValidationSchema = Yup.object<CustomerFormData>({
  // basic info section
  id: Yup.number().nullable(),
  companyName: Yup.string().trim().required('Company name is required'),
  customerTypeId: Yup.number().required('Customer type is required'),
  primaryPhone: Yup.string()
    .transform((value) => {
      // Normalize empty strings to null for consistent validation
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      // Trim whitespace from phone numbers
      return typeof value === 'string' ? value.trim() : value;
    })
    .nullable()
    .optional(),
  primaryEmail: Yup.string()
    .transform((value) => {
      // Normalize empty strings to null for consistent validation
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      // Trim whitespace from emails
      return typeof value === 'string' ? value.trim() : value;
    })
    .nullable()
    .optional()
    .test('email-format', 'Invalid email format', function (value) {
      // Only validate email format if value exists
      if (!value) return true;
      return EMAIL_REGEX.test(value);
    }),
  accountTypeId: Yup.number().required('Account type is required'),

  // address section - using LocationFormValidationSchema
  ...LocationFormValidationSchema.fields,

  // contacts section
  contacts: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      firstName: Yup.string().required('First Name is required'),
      lastName: Yup.string().required('Last Name is required'),
      title: Yup.string().nullable(),
      contactInformation: Yup.array()
        .min(1, 'At least one contact is required')
        .of(
          Yup.object().shape({
            contact: Yup.string()
              .required('Contact information is required')
              .test('is-valid-contact', function (value) {
                if (!value) return true; // Skip format validation if value is empty (required validation will catch this)

                const type = this.parent.type;

                if (type === ContactInfoType.EMAIL) {
                  if (!EMAIL_REGEX.test(value)) {
                    return this.createError({ message: 'Must be a valid email' });
                  }
                } else if (PHONE_CONTACT_TYPES.includes(type)) {
                  if (!PHONE_REGEX.test(value)) {
                    return this.createError({ message: 'Must be a valid phone number' });
                  }
                }

                return true;
              }),
            type: Yup.string().required('Type is required')
          })
        )
    })
  ),

  // pricing and credit section
  pricingSchemeId: Yup.number().nullable(),
  termId: Yup.number().nullable(),
  maxCredit: Yup.number().min(0, 'Max credit cannot be negative'),
  customerDiscount: Yup.number().min(0, 'Discount cannot be negative'),
  salesRegionId: Yup.number().nullable(),
  salesAgentId: Yup.number().required('Sales Agent is required'),
  taxNumber: Yup.string(),

  // notes section
  notes: Yup.string()
});

// Validation schema for the CustomerDrawerForm (drawer/modal form)
export const CustomerDrawerValidationSchema = Yup.object<CustomerFormData>({
  // basic info section
  id: Yup.number().nullable(),
  companyName: Yup.string().trim().required('Company name is required'),
  customerTypeId: Yup.number().required('Customer type is required'),
  primaryPhone: Yup.string()
    .transform((value) => {
      // Normalize empty strings to null for consistent validation
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      // Trim whitespace from phone numbers
      return typeof value === 'string' ? value.trim() : value;
    })
    .nullable()
    .optional(),
  primaryEmail: Yup.string()
    .transform((value) => {
      // Normalize empty strings to null for consistent validation
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      // Trim whitespace from emails
      return typeof value === 'string' ? value.trim() : value;
    })
    .nullable()
    .optional()
    .test('email-format', 'Invalid email format', function (value) {
      // Only validate email format if value exists
      if (!value) return true;
      return EMAIL_REGEX.test(value);
    }),
  accountTypeId: Yup.number().required('Account type is required'),

  // address fields - optional for drawer form
  address: Yup.string().optional(),
  city: Yup.string().optional(),
  state: Yup.string().optional(),
  country: Yup.string().optional(),
  zipCode: Yup.string().optional(),

  // contacts section
  contacts: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      firstName: Yup.string().required('First Name is required'),
      lastName: Yup.string().required('Last Name is required'),
      title: Yup.string().nullable(),
      contactInformation: Yup.array()
        .min(1, 'At least one contact is required')
        .of(
          Yup.object().shape({
            contact: Yup.string()
              .required('Contact information is required')
              .test('is-valid-contact', function (value) {
                if (!value) return true; // Skip format validation if value is empty (required validation will catch this)

                const type = this.parent.type;

                if (type === ContactInfoType.EMAIL) {
                  if (!EMAIL_REGEX.test(value)) {
                    return this.createError({ message: 'Must be a valid email' });
                  }
                } else if (PHONE_CONTACT_TYPES.includes(type)) {
                  if (!PHONE_REGEX.test(value)) {
                    return this.createError({ message: 'Must be a valid phone number' });
                  }
                }

                return true;
              }),
            type: Yup.string().required('Type is required')
          })
        )
    })
  ),

  // pricing and credit section
  pricingSchemeId: Yup.number().nullable(),
  termId: Yup.number().nullable(),
  maxCredit: Yup.number().min(0, 'Max credit cannot be negative'),
  customerDiscount: Yup.number().min(0, 'Discount cannot be negative'),
  salesRegionId: Yup.number().nullable(),
  salesAgentId: Yup.number().required('Sales Agent is required'),
  taxNumber: Yup.string(),

  // notes section
  notes: Yup.string()
});

// Validation schema for the CustomerModalForm (new modal form with mandatory address fields)
export const CustomerModalValidationSchema = Yup.object<CustomerFormData>({
  // basic info section
  id: Yup.number().nullable(),
  companyName: Yup.string().trim().required('Company name is required'),
  customerTypeId: Yup.number().required('Customer type is required'),
  primaryPhone: Yup.string()
    .transform((value) => {
      // Normalize empty strings to null for consistent validation
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      // Trim whitespace from phone numbers
      return typeof value === 'string' ? value.trim() : value;
    })
    .nullable()
    .optional(),
  primaryEmail: Yup.string()
    .transform((value) => {
      // Normalize empty strings to null for consistent validation
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      // Trim whitespace from emails
      return typeof value === 'string' ? value.trim() : value;
    })
    .nullable()
    .optional()
    .test('email-format', 'Invalid email format', function (value) {
      // Only validate email format if value exists
      if (!value) return true;
      return EMAIL_REGEX.test(value);
    }),
  accountTypeId: Yup.number().required('Account type is required'),

  // address fields - mandatory for modal form
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().max(3, 'State must have maximum 3 letters').required('State is required'),
  country: Yup.string().required('Country is required'),
  zipCode: Yup.string().max(10, 'ZIP Code must have maximum 10 characters').required('ZIP Code is required'),

  // contacts section
  contacts: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      firstName: Yup.string().required('First Name is required'),
      lastName: Yup.string().required('Last Name is required'),
      title: Yup.string().nullable(),
      contactInformation: Yup.array()
        .min(1, 'At least one contact is required')
        .of(
          Yup.object().shape({
            contact: Yup.string()
              .required('Contact information is required')
              .test('is-valid-contact', function (value) {
                if (!value) return true; // Skip format validation if value is empty (required validation will catch this)

                const type = this.parent.type;

                if (type === ContactInfoType.EMAIL) {
                  if (!EMAIL_REGEX.test(value)) {
                    return this.createError({ message: 'Must be a valid email' });
                  }
                } else if (PHONE_CONTACT_TYPES.includes(type)) {
                  if (!PHONE_REGEX.test(value)) {
                    return this.createError({ message: 'Must be a valid phone number' });
                  }
                }

                return true;
              }),
            type: Yup.string().required('Type is required')
          })
        )
    })
  ),

  // pricing and credit section
  pricingSchemeId: Yup.number().nullable(),
  termId: Yup.number().nullable(),
  maxCredit: Yup.number().min(0, 'Max credit cannot be negative'),
  customerDiscount: Yup.number().min(0, 'Discount cannot be negative'),
  salesRegionId: Yup.number().nullable(),
  salesAgentId: Yup.number().required('Sales Agent is required'),
  taxNumber: Yup.string(),

  // notes section
  notes: Yup.string()
});

// Simplified validation schema for quick customer creation from job panel
export const QuickCustomerValidationSchema = Yup.object<CustomerFormData>({
  companyName: Yup.string().required('Company name is required'),
  customerTypeId: Yup.number().required('Customer type is required'),
  primaryEmail: Yup.string().email('Invalid email format').nullable(),
  primaryPhone: Yup.string().nullable(),
  // Location fields - optional for quick customer creation
  address: Yup.string().optional(),
  city: Yup.string().optional(),
  state: Yup.string().optional(),
  country: Yup.string().optional(),
  zipCode: Yup.string().optional(),
  latitude: Yup.number().optional(),
  longitude: Yup.number().optional()
});
